import React, { useMemo } from 'react';
import { useTracks, ParticipantTile } from '@livekit/components-react';
import { Track } from 'livekit-client';
import PinScatter from '../design/components/PinScatter';
import { playerStyleFor } from '../design/themes';

const MAX_GUEST_TILES = 4;

const stageBg = '#0b3d2e';
const inkColor = '#f7f4ea';
const accentYellow = '#f4c430';

const Tile = ({ trackRef, label, large = false }) => (
  <div
    style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      background: '#000',
      border: `3px solid ${inkColor}`,
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: '6px 6px 0 rgba(0,0,0,0.4)',
    }}
  >
    {trackRef ? <ParticipantTile trackRef={trackRef} disableSpeakingIndicator /> : null}
    <div
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: large ? '10px 14px' : '6px 10px',
        background: 'rgba(0,0,0,0.55)',
        color: inkColor,
        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
        fontSize: large ? 18 : 14,
        letterSpacing: 1,
      }}
    >
      {label}
    </div>
  </div>
);

const ScoreRow = ({ scores, hostPlayerId }) => {
  if (!scores || scores.length === 0) return null;
  const sorted = [...scores].sort((a, b) => b.score - a.score);
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
      {sorted.map((s, i) => (
        <div
          key={s.id}
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'baseline',
            padding: '6px 12px',
            background: i === 0 ? accentYellow : 'rgba(0,0,0,0.35)',
            color: i === 0 ? '#161315' : inkColor,
            borderRadius: 8,
            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
            fontSize: 22,
            letterSpacing: 1,
          }}
        >
          <span style={{ fontSize: 14, opacity: 0.7 }}>
            {i === 0 ? '👑' : `#${i + 1}`}
          </span>
          <span>
            {(s.id || '').toString().toUpperCase()}
            {s.id === hostPlayerId ? ' ·HOST' : ''}
          </span>
          <span style={{ fontWeight: 700 }}>{s.score}</span>
        </div>
      ))}
    </div>
  );
};

const StagePlaceholder = ({ message }) => (
  <div
    style={{
      width: '100%',
      height: '100%',
      border: `3px dashed ${inkColor}33`,
      borderRadius: 16,
      display: 'grid',
      placeItems: 'center',
      color: `${inkColor}66`,
      fontFamily: 'ui-monospace, monospace',
      fontSize: 24,
      letterSpacing: 2,
      textAlign: 'center',
      padding: 40,
    }}
  >
    {message}
  </div>
);

const Stage = ({ round, results, players }) => {
  if (!round && !results) {
    return <StagePlaceholder message="WAITING FOR FIRST ROUND" />;
  }
  const data = results || round;
  const cricketer = data.cricketer || {};

  const pinList = results
    ? (results.results || []).map((r) => {
        const idx = players.indexOf(r.playerId);
        const palette = playerStyleFor(idx >= 0 ? idx : 0);
        return {
          x: r.guess?.x ?? 50,
          y: r.guess?.y ?? 50,
          color: palette.color,
          emoji: palette.emoji,
        };
      })
    : [];

  const status = results ? `ROUND ${results.currentRound} · RESULTS` : `ROUND ${round.currentRound} · LIVE`;

  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'stretch', width: '100%', height: '100%' }}>
      <div
        style={{
          flexShrink: 0,
          height: '100%',
          aspectRatio: '1 / 1',
          maxWidth: '100%',
        }}
      >
        <PinScatter
          xAxis={data.xAxis || 'X'}
          yAxis={data.yAxis || 'Y'}
          xRange={data.xRange || { min: 0, max: 100 }}
          yRange={data.yRange || { min: 0, max: 100 }}
          opponentPins={pinList}
          correctPosition={results?.correctPosition}
          isInteractive={false}
          staggerOpponents={!!results}
          topLabel="LIVE BROADCAST"
          topTitle={(cricketer.name || '—').toString().toUpperCase()}
          topBadge={(cricketer.role || 'CAREER').toString().toUpperCase()}
        />
      </div>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: 14,
            letterSpacing: 1.5,
            opacity: 0.6,
          }}
        >
          {status}
        </div>
        <div
          style={{
            fontFamily: 'system-ui, sans-serif',
            fontSize: 42,
            fontWeight: 700,
            lineHeight: 1.05,
            color: inkColor,
          }}
        >
          {(cricketer.name || '—').toString().toUpperCase()}
        </div>
        <div
          style={{
            display: 'inline-block',
            alignSelf: 'flex-start',
            padding: '6px 14px',
            background: accentYellow,
            color: '#161315',
            borderRadius: 999,
            fontFamily: 'ui-monospace, monospace',
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: 1,
          }}
        >
          {(cricketer.role || 'CAREER').toString().toUpperCase()}
        </div>
        <div
          style={{
            marginTop: 8,
            padding: 14,
            border: `2px solid ${inkColor}33`,
            borderRadius: 10,
            fontFamily: 'ui-monospace, monospace',
            fontSize: 16,
            lineHeight: 1.5,
            color: `${inkColor}cc`,
          }}
        >
          <div style={{ opacity: 0.6, fontSize: 12, letterSpacing: 1.2, marginBottom: 6 }}>AXES</div>
          <div>X · {(data.xAxis || '').toString().toUpperCase()}</div>
          <div>Y · {(data.yAxis || '').toString().toUpperCase()}</div>
        </div>
        {results && (
          <div
            style={{
              marginTop: 'auto',
              padding: 14,
              background: 'rgba(0,0,0,0.35)',
              borderRadius: 10,
              fontFamily: 'ui-monospace, monospace',
              fontSize: 14,
            }}
          >
            <div style={{ opacity: 0.6, marginBottom: 6, letterSpacing: 1 }}>THIS ROUND</div>
            {(results.results || [])
              .slice()
              .sort((a, b) => (a.distance ?? 999) - (b.distance ?? 999))
              .map((r) => (
                <div key={r.playerId} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                  <span>{(r.playerId || '').toString().toUpperCase()}</span>
                  <span style={{ opacity: 0.7 }}>
                    +{r.score} · {Math.round(r.distance ?? 0)}u away
                  </span>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

const BroadcastLayout = ({
  gameId,
  scores,
  currentRound,
  totalRounds,
  hostPlayerId,
  round,
  results,
  players,
}) => {
  const tracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: false }],
    { onlySubscribed: false }
  );

  const { hostTrack, guestTracks } = useMemo(() => {
    const tagged = tracks.filter((t) => {
      const id = t.participant?.identity || '';
      return id && !id.startsWith('obs-');
    });
    const host = tagged.find((t) => t.participant.identity === hostPlayerId) || null;
    const guests = tagged
      .filter((t) => t.participant.identity !== hostPlayerId)
      .slice(0, MAX_GUEST_TILES);
    return { hostTrack: host, guestTracks: guests };
  }, [tracks, hostPlayerId]);

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        background: stageBg,
        color: inkColor,
        display: 'grid',
        gridTemplateRows: '110px 1fr 320px',
        gridTemplateColumns: '1fr 380px',
        gap: 18,
        padding: 24,
        boxSizing: 'border-box',
        overflow: 'hidden',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <header style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 18 }}>
          <div
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: 32,
              letterSpacing: 2,
              fontWeight: 700,
            }}
          >
            GUESS THE SPOT
          </div>
          <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 20, opacity: 0.65 }}>
            ROOM {String(gameId || '').toUpperCase()}
          </div>
          {currentRound > 0 && (
            <div
              style={{
                padding: '6px 14px',
                background: accentYellow,
                color: '#161315',
                borderRadius: 8,
                fontFamily: 'ui-monospace, monospace',
                fontSize: 24,
                fontWeight: 700,
                letterSpacing: 1,
              }}
            >
              ROUND {currentRound} / {totalRounds}
            </div>
          )}
        </div>
        <ScoreRow scores={scores} hostPlayerId={hostPlayerId} />
      </header>

      <section style={{ minWidth: 0, minHeight: 0 }}>
        <Stage round={round} results={results} players={players} />
      </section>

      <aside style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
        <div
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: 14,
            letterSpacing: 1.5,
            opacity: 0.65,
          }}
        >
          HOST
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          <Tile
            trackRef={hostTrack}
            label={hostPlayerId ? hostPlayerId.toString().toUpperCase() : 'WAITING…'}
            large
          />
        </div>
      </aside>

      <section
        style={{
          gridColumn: '1 / -1',
          display: 'grid',
          gridTemplateColumns: `repeat(${MAX_GUEST_TILES}, 1fr)`,
          gap: 16,
        }}
      >
        {Array.from({ length: MAX_GUEST_TILES }).map((_, i) => {
          const trackRef = guestTracks[i];
          const label = trackRef
            ? trackRef.participant.identity.toString().toUpperCase()
            : `GUEST ${i + 1}`;
          return <Tile key={trackRef ? trackRef.participant.identity : `slot-${i}`} trackRef={trackRef} label={label} />;
        })}
      </section>
    </div>
  );
};

export default BroadcastLayout;
